import { useCallback, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Building2, Landmark, Loader2, MapPin, Hash } from "lucide-react";
import {
  DocumentHeader,
  DocumentPage,
  DocumentStickyBar,
  useDocumentSaveShortcut,
} from "@/components/shared/document-shell";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid, FormSection } from "@/components/shared/form";
import { Button } from "@/components/ui/button";
import { Input, NativeSelect } from "@/components/ui/input";
import { INDIAN_STATES } from "@/features/clients/constants";
import {
  companyToForm,
  useCompany,
  useCreateCompany,
  useUpdateCompany,
} from "@/features/settings/api";
import { previewDocNumber } from "@/features/settings/constants";
import {
  companyFormSchema,
  emptyCompanyForm,
  type CompanyFormValues,
} from "@/features/settings/schema";

export function CompanySettingsPage() {
  return (
    <ErrorBoundary title="Company settings crashed">
      <CompanySettingsInner />
    </ErrorBoundary>
  );
}

function CompanySettingsInner() {
  const { data: company, isLoading } = useCompany();
  const updateCompany = useUpdateCompany();
  const createCompany = useCreateCompany();

  const form = useForm<CompanyFormValues>({
    resolver: zodResolver(companyFormSchema),
    defaultValues: emptyCompanyForm,
    mode: "onBlur",
  });

  const { errors, isDirty } = form.formState;
  const watched = useWatch({ control: form.control });
  const fyMonth = company?.fiscal_year_start_month ?? 4;

  useEffect(() => {
    if (company) form.reset(companyToForm(company));
  }, [company, form]);

  const submit = form.handleSubmit(async (values) => {
    if (company) {
      await updateCompany.mutateAsync({ id: company.id, values });
      return;
    }
    await createCompany.mutateAsync(values);
  });

  const save = useCallback(() => {
    void submit();
  }, [submit]);

  const saving = updateCompany.isPending || createCompany.isPending;
  useDocumentSaveShortcut(save, !saving && (company ? isDirty : true));

  if (isLoading) {
    return <p className="py-12 text-center text-sm text-muted-foreground">Loading company…</p>;
  }

  return (
    <DocumentPage className="max-w-3xl pb-16">
      <DocumentHeader
        title="Company"
        subtitle="Printed on quotations, invoices and POs · Ctrl+S save"
        actions={
          <Button
            type="button"
            size="sm"
            disabled={saving || (company ? !isDirty : false)}
            onClick={save}
          >
            {saving ? <Loader2 className="animate-spin" /> : null}
            Save
          </Button>
        }
      />

      <form
        className="space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <FormSection id="identity" title="Identity" variant="plain" icon={<Building2 />}>
          <FieldGrid>
            <Field label="Legal name" required span={8} error={errors.name?.message} htmlFor="co-name">
              <Input id="co-name" {...form.register("name")} />
            </Field>
            <Field label="GSTIN" span={4} htmlFor="co-gstin">
              <Input id="co-gstin" className="uppercase" maxLength={15} {...form.register("gstin")} />
            </Field>
            <Field label="PAN" span={4} htmlFor="co-pan">
              <Input id="co-pan" className="uppercase" maxLength={10} {...form.register("pan_no")} />
            </Field>
            <Field label="CIN" span={4} htmlFor="co-cin">
              <Input id="co-cin" className="uppercase" {...form.register("cin_no")} />
            </Field>
            <Field label="Phone" span={4} htmlFor="co-phone">
              <Input id="co-phone" {...form.register("phone")} />
            </Field>
            <Field label="Email" span={6} error={errors.email?.message} htmlFor="co-email">
              <Input id="co-email" type="email" {...form.register("email")} />
            </Field>
            <Field label="Website" span={6} htmlFor="co-web">
              <Input id="co-web" {...form.register("website")} />
            </Field>
          </FieldGrid>
        </FormSection>

        <FormSection id="address" title="Address" variant="plain" icon={<MapPin />}>
          <FieldGrid>
            <Field label="Address line 1" span={12} htmlFor="co-a1">
              <Input id="co-a1" {...form.register("addr1")} />
            </Field>
            <Field label="Address line 2" span={12} htmlFor="co-a2">
              <Input id="co-a2" {...form.register("addr2")} />
            </Field>
            <Field label="City" span={4} htmlFor="co-city">
              <Input id="co-city" {...form.register("city")} />
            </Field>
            <Field label="State" span={4} htmlFor="co-state">
              <NativeSelect id="co-state" {...form.register("state")}>
                <option value="">Select…</option>
                {INDIAN_STATES.map((s) => (
                  <option key={s.code} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label="PIN" span={2} htmlFor="co-pin">
              <Input id="co-pin" {...form.register("pincode")} />
            </Field>
            <Field label="Country" span={2} htmlFor="co-country">
              <Input id="co-country" {...form.register("country")} />
            </Field>
          </FieldGrid>
        </FormSection>

        <FormSection
          id="prefixes"
          title="Document prefixes"
          description={`Pattern {PREFIX}-{FY}-#### · e.g. ${previewDocNumber(watched.invoice_prefix || "INV", fyMonth)}`}
          variant="plain"
          icon={<Hash />}
        >
          <FieldGrid>
            <Field label="Client" span={2} htmlFor="pfx-cl">
              <Input id="pfx-cl" className="uppercase" {...form.register("client_prefix")} />
            </Field>
            <Field label="Vendor" span={2} htmlFor="pfx-vn">
              <Input id="pfx-vn" className="uppercase" {...form.register("vendor_prefix")} />
            </Field>
            <Field label="PO" span={2} htmlFor="pfx-po">
              <Input id="pfx-po" className="uppercase" {...form.register("po_prefix")} />
            </Field>
            <Field label="Quotation" span={2} htmlFor="pfx-qt">
              <Input id="pfx-qt" className="uppercase" {...form.register("quotation_prefix")} />
            </Field>
            <Field label="SO" span={2} htmlFor="pfx-so">
              <Input id="pfx-so" className="uppercase" {...form.register("so_prefix")} />
            </Field>
            <Field label="Invoice" span={2} htmlFor="pfx-inv">
              <Input id="pfx-inv" className="uppercase" {...form.register("invoice_prefix")} />
            </Field>
            <Field label="Credit note" span={2} htmlFor="pfx-cn">
              <Input id="pfx-cn" className="uppercase" {...form.register("credit_note_prefix")} />
            </Field>
            <Field label="Dispatch" span={2} htmlFor="pfx-dn">
              <Input id="pfx-dn" className="uppercase" {...form.register("dispatch_prefix")} />
            </Field>
            <Field label="Plan" span={2} htmlFor="pfx-plan">
              <Input id="pfx-plan" className="uppercase" {...form.register("plan_prefix")} />
            </Field>
          </FieldGrid>
        </FormSection>

        <FormSection
          id="bank"
          title="Bank details"
          description="Optional — shown on invoices later"
          variant="plain"
          icon={<Landmark />}
        >
          <FieldGrid>
            <Field label="Bank name" span={6} htmlFor="bank-name">
              <Input id="bank-name" {...form.register("bank_name")} />
            </Field>
            <Field label="Account no." span={3} htmlFor="bank-ac">
              <Input id="bank-ac" {...form.register("bank_account")} />
            </Field>
            <Field label="IFSC" span={3} htmlFor="bank-ifsc">
              <Input id="bank-ifsc" className="uppercase" {...form.register("bank_ifsc")} />
            </Field>
          </FieldGrid>
        </FormSection>
      </form>

      <DocumentStickyBar>
        <span className="text-2xs text-muted-foreground">
          {isDirty || !company ? "Unsaved changes" : "All changes saved"}
        </span>
        <Button
          type="button"
          size="sm"
          disabled={saving || (company ? !isDirty : false)}
          onClick={save}
        >
          {saving ? <Loader2 className="animate-spin" /> : null}
          Save company
        </Button>
      </DocumentStickyBar>
    </DocumentPage>
  );
}
